import { BalanceData, LevelPhase, LevelSim, SpawnEntry } from "@highmarch/domain";
import { EnemyRole, ROLE_LABEL, colourBlind, enemyLook } from "../render/views/enemyLook";
import { badgeDataUrl } from "../render/views/enemyGlyphs";
import { icon } from "./iconsKit";
import { IMMUNE_STATUS } from "../render/views/enemyLook";
import { RARE_CELL, STATUS_CELL, glyphDataUrl } from "../render/views/enemyGlyphs";
import { STATUS_COLOUR } from "../render/views/enemyLook";
import { entryImmunity, immunityKinds, immunityLine, isRareArchetype } from "../rare/rareApi";
import { WYRM_COLOURS, isWyrmArchetype } from "../rare/wyrmApi";

/** One type in a wave: its badge, how many, and whether it is the boss. */
export interface WaveChip {
  readonly archetype: number; readonly role: EnemyRole; readonly colour: number; readonly count: number; readonly boss: boolean; readonly name: string;
  /** M36: the immunity these carry (Immunity bits; 0 none): a group of their own, its glyph on the chip. */
  readonly immunity: number;
  /** M36: the rare spawn (its own gold chip). */
  readonly rare: boolean;
  /** M40: the Starfall Wyrm (its own prismatic chip); absent for everything else. */
  readonly legend?: true;
}

/**
 * A wave's composition by archetype, in order of first arrival over the whole wave (the boss last, as it comes), counting
 * only entries from `from` on. Reads whatever the
 * balance data schedules — any number of types, any mix — so levels never need a line here.
 */
export function waveChips(balance: BalanceData, entries: readonly SpawnEntry[], from = 0): WaveChip[] {
  // The order is set by the whole wave (first arrival over every entry), only the counts by what is still to come:
  // as the portal empties a type just drops out, and the rest never shuffle (Kevin, 10-07: "the order should be the same").
  const byKey = new Map<string, { archetype: number; boss: boolean; count: number; immunity: number }>();
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i], imm = entryImmunity(e), key = `${e.archetype}|${e.isBoss}|${imm}`;
    let hit = byKey.get(key);
    if (!hit) byKey.set(key, (hit = { archetype: e.archetype, boss: e.isBoss, count: 0, immunity: imm }));
    if (i >= from) hit.count++;
  }
  return [...byKey.values()].filter((g) => g.count > 0).sort((a, b) => Number(a.boss) - Number(b.boss)).map((g) => {
    const rare = isRareArchetype(balance, g.archetype), legend = isWyrmArchetype(balance, g.archetype);
    const a = balance.enemies[g.archetype], look = enemyLook(a, g.boss || rare);
    return { archetype: g.archetype, role: look.role, colour: look.colour, count: g.count, boss: g.boss || rare, name: a.name, immunity: g.immunity, rare, ...(legend ? { legend: true as const } : {}) };
  });
}

/** Chips as HTML: the in-world badge, then the count (bosses by name). */
export function chipsHtml(chips: readonly WaveChip[], px = 22): string {
  return chips.map((c) => {
    // M36: an immune group shows its immunity's shield after the badge; the rare its gold star and name
    const imm = immunityKinds(c.immunity).map((k) => `<img class="imm" src="${glyphDataUrl(STATUS_CELL[IMMUNE_STATUS[k]], STATUS_COLOUR[IMMUNE_STATUS[k]], 32)}" width="16" height="16" alt="${k}">`).join("");
    const title = `${c.count} ${c.boss ? c.name : ROLE_LABEL[c.role]}${c.immunity ? ` · ${immunityLine(c.immunity).toLowerCase()}` : ""}`;
    // M40: the Starfall Wyrm's chip: a lilac star and LEGEND before its name
    const badge = c.legend ? glyphDataUrl(RARE_CELL, WYRM_COLOURS.lilac, 44) : c.rare ? glyphDataUrl(RARE_CELL, 0xffd75c, 44) : badgeDataUrl(c.role, c.colour);
    const label = c.legend ? `LEGEND · ${c.name}` : c.rare ? `RARE · ${c.name}` : c.name;
    return `<span class="wchip${c.boss ? " boss" : ""}${c.rare ? " rare" : ""}${c.legend ? " legend" : ""}${c.immunity ? " immune" : ""}" data-a="${c.archetype}" data-boss="${c.boss ? 1 : 0}" data-imm="${c.immunity}" title="${title}"><img src="${badge}" width="${px}" height="${px}" alt="${c.legend ? "legend" : c.rare ? "rare" : c.boss ? "boss" : ROLE_LABEL[c.role]}">${imm}${c.boss ? `<b>${label}</b>` : c.count > 1 ? `×${c.count}` : ""}</span>`;
  }).join("");
}

/**
 * The wave strip over the control row: before a wave, what the next one brings; during it, what is still to come
 * out of the portal, then — once the portal is empty — the wave after. Rebuilt only when its content changes (a
 * spawn, a new stage, the palette), never per frame.
 */
export class WavePreviewView {
  readonly root: HTMLDivElement;
  /** What the strip last showed, as one number (phase, stage, spawned so far, palette): compared every frame. */
  private shown = -1;

  /** Set by the HUD: a chip was tapped (QoL R1) — the enemy card for that type. */
  onChip: ((archetype: number, boss: boolean, immunity: number) => void) | null = null;

  constructor() {
    this.root = document.createElement("div");
    this.root.className = "wave-preview hidden";
    this.root.addEventListener("click", (e) => {
      const chip = (e.target as Element | null)?.closest<HTMLElement>(".wchip");
      if (chip?.dataset.a !== undefined) this.onChip?.(Number(chip.dataset.a), chip.dataset.boss === "1", Number(chip.dataset.imm ?? 0));
    });
  }

  refresh(sim: LevelSim): void {
    const cb = colourBlind();
    const sig = ((sim.phase * 1000 + sim.stage) * 1024 + sim.spawnedThisStage) * 2 + (cb ? 1 : 0);
    if (sig === this.shown) return;
    this.shown = sig;
    let stage = 0, from = 0, label = "";
    const running = sim.phase === LevelPhase.WaveRunning;
    if (running && sim.spawnedThisStage < sim.scheduledThisStage) { stage = sim.stage; from = sim.spawnedThisStage; label = "Still coming"; }
    else if ((running || sim.phase === LevelPhase.BetweenWaves || sim.phase === LevelPhase.Ready) && sim.stage < sim.stages) { stage = sim.stage + 1; label = sim.isIron ? "The Iron wave" : `Wave ${sim.stage + 1}`; }
    if (stage === 0) { this.root.className = "wave-preview hidden"; return; }
    const entries = stage === sim.stage && running ? sim.schedule : sim.previewStage(stage);
    const chips = waveChips(sim.balance, entries, from);
    const boss = chips.some((c) => c.boss);
    this.root.className = `wave-preview plate${boss ? " has-boss" : ""}`;
    this.root.innerHTML = `<small class="lbl">${boss ? `${icon("skull", 12)} BOSS · ` : ""}${label}</small>${chipsHtml(chips)}`;
  }
}
